import { useState, useEffect, useCallback, useRef } from 'react';
import React from 'react';
import { 
  ChevronLeft, Save, Download, Eye, RotateCcw, 
  Loader2, AlertCircle, PanelBottomClose, PanelBottomOpen, Subtitles,
  Film, Plus, Zap, Check, X, Music, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { VideoPreviewPanel } from './VideoPreviewPanel';
import { VideoEditorSidebar } from './VideoEditorSidebar';
import { TimelinePanel } from './TimelinePanel';
import { getDefaultVideoSettings, CAPTION_LANGUAGES } from './editorConfig';

import { reelsApi, driveApi, transcribeApi, getAuthToken } from '@/services/api';

const generateMockTimelineClips = (duration) => {
  if (!duration || duration <= 0) return [{ id: 'clip-1', name: 'Main Clip', startTime: 0, endTime: 30 }];
  const count = Math.max(1, Math.min(5, Math.floor(duration / 12)));
  const clips = []; let cursor = 0;
  for (let i = 0; i < count; i++) {
    const remaining = duration - cursor;
    const segDur = i === count - 1 ? remaining : Math.floor((remaining / (count - i)) * (0.7 + Math.random() * 0.5));
    clips.push({ id: `clip-${i + 1}`, name: `Segment ${i + 1}`, startTime: cursor, endTime: cursor + segDur });
    cursor += segDur;
  }
  return clips;
};

const MOCK_PHRASES_ES = ['Esto es increíble', 'No te lo puedes perder', '¿Sabías esto?', 'Mira lo que pasa', 'El secreto está aquí', 'Cambia todo', 'Lo que nadie te dice', '¡Increíble resultado!', 'Prueba esto hoy', 'El truco definitivo', 'Así de simple', 'Importante saberlo'];
const generateMockSubtitles = (duration) => {
  const subs = []; let t = 0, i = 0;
  while (t < duration - 0.5) {
    const len = 1.5 + Math.random() * 2.5;
    subs.push({ id: `sub-${Date.now()}-${i}`, text: MOCK_PHRASES_ES[i % MOCK_PHRASES_ES.length], startTime: t, endTime: Math.min(t + len, duration), position: { x: 50, y: 82 }, color: '#ffffff', fontSize: 18, fontFamily: 'default', isSubtitle: true });
    t += len + 0.1; i++;
  }
  return subs;
};

/**
 * VideoEditorPage
 * 
 * Main video editor interface. Reusable across all modules.
 * Module type determines which controls are visible.
 */
export const VideoEditorPage = ({ 
  video,
  moduleType = 'full', // 'youtube' | 'reels' | 'talking' | 'full'
  reelProject = null,  // existing reel project (for editing)
  onSave,
  onClose,
  onExport,
}) => {
  const { trialActive, activeBrand } = useApp();
  
  // Reel-specific state ─────────────────────────────────────────────────────
  const [reelVideo, setReelVideo] = useState(video || null); // generated reel video
  const [clipPickerOpen, setClipPickerOpen] = useState(moduleType === 'reels' && !video);
  const [libraryClips, setLibraryClips] = useState([]);
  const [clipScores, setClipScores] = useState({});
  const [selectedClipIds, setSelectedClipIds] = useState([]);
  const [reelGenerating, setReelGenerating] = useState(false);
  const [reelProgress, setReelProgress] = useState(0);
  const [reelProgressMsg, setReelProgressMsg] = useState('Iniciando...');
  const [reelConcept, setReelConcept] = useState(null);
  const pollingRef = useRef(null);

  // Effective video = generated reel OR passed-in video
  const effectiveVideo = reelVideo;

  // Load library clips when clip picker opens
  useEffect(() => {
    if (clipPickerOpen && activeBrand?.id && libraryClips.length === 0) {
      loadLibraryClips();
    }
  }, [clipPickerOpen, activeBrand]); // eslint-disable-line

  const loadLibraryClips = async () => {
    try {
      const files = await driveApi.listFiles({ brandId: activeBrand?.id, limit: 50 });
      const videos = files.filter(f => f.duration_seconds > 0 || f.name?.match(/\.(mp4|mov|avi|webm)/i));
      setLibraryClips(videos);
      if (videos.length > 0) {
        try {
          setClipScores(await reelsApi.scoreClips({ video_ids: videos.map(v => v.id), brand_id: activeBrand?.id }));
        } catch { /* silent */ }
      }
    } catch { toast.error('Error cargando biblioteca'); }
  };

  const handleGenerateReel = async () => {
    if (selectedClipIds.length === 0) { toast.error('Selecciona al menos un clip'); return; }
    setReelGenerating(true);
    setReelProgress(5);
    setReelProgressMsg('Generando concepto viral...');
    try {
      // 1. Get AI concept
      let concept = null;
      try {
        concept = await reelsApi.generateConcept({ video_ids: selectedClipIds, brand_id: activeBrand?.id, language: 'es' });
        setReelConcept(concept);
      } catch { /* use no hook */ }

      // 2. Create reel project
      setReelProgress(15); setReelProgressMsg('Creando proyecto...');
      const project = await reelsApi.create({
        name: concept?.caption || `Reel ${new Date().toLocaleDateString('es')}`,
        brand_id: activeBrand?.id,
        source_video_ids: selectedClipIds,
        hook_text: concept?.caption || '',
      });

      // 3. Start generation
      setReelProgress(25); setReelProgressMsg('Ensamblando clips...');
      await reelsApi.generate(project.id);

      // 4. Poll for result
      const msgs = ['Extrayendo mejores momentos...', 'Aplicando cortes rápidos...', 'Efecto bucle...', 'Normalizando formato...', 'Renderizando...'];
      pollingRef.current = setInterval(async () => {
        try {
          const data = await reelsApi.get(project.id);
          if (data.status === 'processing') {
            setReelProgress(p => Math.min(88, p + 3));
            setReelProgressMsg(msgs[Math.floor(Math.random() * msgs.length)]);
          } else if (data.status === 'ready') {
            clearInterval(pollingRef.current);
            setReelProgress(100); setReelProgressMsg('¡Listo!');
            // Load generated video into editor
            const newVideo = {
              id: data.result_video_id || project.id,
              title: project.name,
              name: project.name,
              url: data.result_url,
              durationSeconds: 30,
              originalStartTime: 0,
            };
            setReelVideo(newVideo);
            setClipPickerOpen(false);
            setReelGenerating(false);
            // Set clips in timeline
            const dur = 30;
            setClips([{ id: 'reel-1', name: 'Reel', startTime: 0, endTime: dur }]);
            setSettings(s => ({ ...s, title: project.name, duration: dur, endTime: dur }));
            toast.success('Reel generado. Ahora puedes editarlo.');
            // Auto-trigger subtitles
            setTimeout(() => setSubsOpen(true), 800);
          } else if (data.status === 'error') {
            clearInterval(pollingRef.current);
            setReelGenerating(false);
            toast.error('Error al generar el reel. Intenta con clips más largos (>3s cada uno).');
          }
        } catch { /* polling error — keep trying */ }
      }, 2500);
    } catch (e) {
      setReelGenerating(false);
      toast.error(e.message || 'Error al generar el reel');
    }
  };

  // Music track in timeline (reels)
  const [timelineMusicTrack, setTimelineMusicTrack] = useState(null);
  const [musicClipState, setMusicClipState] = useState(null); // current position/volume from timeline

  const handleAddMusicToTimeline = useCallback((track) => {
    setTimelineMusicTrack(track); // null = remove
    if (!track) setMusicClipState(null);
  }, []);
  
  // Settings state
  const [settings, setSettings] = useState(() => ({
    ...getDefaultVideoSettings(),
    title: (reelProject?.name || video?.title || video?.name || ''),
    duration: video?.durationSeconds || 60,
    startTime: video?.startTime || 0,
    endTime: video?.endTime || (video?.durationSeconds || 60),
  }));
  
  // Timeline state
  const [clips, setClips] = useState([]);
  const [brollClips, setBrollClips] = useState([]);
  const [textTracks, setTextTracks] = useState([]);
  const [selectedTextTrack, setSelectedTextTrack] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [timelineVisible, setTimelineVisible] = useState(true);
  const [timelineHeight, setTimelineHeight] = useState(260);
  const timelineHeightRef = React.useRef(260);

  // Subtitle dialog state
  const [subsOpen, setSubsOpen] = useState(false);
  const [subsLang, setSubsLang] = useState('auto');
  const [subsGenerating, setSubsGenerating] = useState(false);
  const [subsConfirm, setSubsConfirm] = useState(false);
  
  // UI state
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const hasChangesRef = React.useRef(false);

  // Global play state — shared between VideoPreviewPanel and TimelinePanel
  const [isPlaying, setIsPlaying] = useState(false);
  const previewPlayRef = React.useRef(null); // holds handlePlayPause from VideoPreviewPanel
  const handlePlayToggle = useCallback(() => previewPlayRef.current?.(), []);

  // Initialize clips when video changes
  useEffect(() => {
    const duration = effectiveVideo?.durationSeconds || 60;
    setClips(generateMockTimelineClips(duration));
  }, [effectiveVideo]);

  // Track changes
  useEffect(() => {
    setHasChanges(true);
  }, [settings]);

  // Sync hasChanges ref (needed by auto-save interval)
  useEffect(() => { hasChangesRef.current = hasChanges; }, [hasChanges]);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!hasChangesRef.current) return;
      setSaving(true);
      try {
        await new Promise(r => setTimeout(r, 300));
        setHasChanges(false);
        setLastSaved(new Date());
      } finally {
        setSaving(false);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle settings change
  const handleSettingsChange = useCallback((newSettings) => {
    setSettings(newSettings);
  }, []);

  // Save changes
  const handleSave = async (silent = false) => {
    setSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      onSave?.({ ...video, settings });
      setHasChanges(false);
      setLastSaved(new Date());
      if (!silent) toast.success('Guardado');
    } catch (error) {
      if (!silent) toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Export video
  const handleExport = async () => {
    if (trialActive && (settings.exportQuality === '4k')) {
      toast.error('4K export requires a Pro subscription');
      return;
    }

    setExporting(true);
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      onExport?.({ ...video, settings });
      toast.success('Video exported successfully!');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Subtitle generation
  const handleAutoSubtitles = () => {
    if (textTracks.length > 0) { setSubsConfirm(true); setSubsOpen(true); return; }
    setSubsOpen(true);
  };
  const doGenerate = async (replace) => {
    setSubsGenerating(true); setSubsConfirm(false);
    try {
      let videoId = effectiveVideo?.id;
      let clipStart = 0;
      let clipEnd = settings.duration || 60;
      try {
        const u = new URL(video?.url || '');
        const pathParts = u.pathname.split('/');
        videoId = pathParts[pathParts.length - 1] || video?.id;
        clipStart = parseFloat(u.searchParams.get('start') || '0');
        clipEnd   = parseFloat(u.searchParams.get('end') || String(settings.duration || 60));
      } catch (_) {}

      const data = await transcribeApi.clip(videoId, {
        start: clipStart, end: clipEnd, language: subsLang,
      });
      setTextTracks(replace ? data.tracks : [...textTracks, ...data.tracks]);
      setSubsOpen(false);
      toast.success(`${data.count} subtítulos generados`);
    } catch (err) {
      toast.error(`Error al generar subtítulos: ${err.message}`);
    } finally {
      setSubsGenerating(false);
    }
  };

  // Reset to defaults
  const handleReset = () => {
    setSettings({
      ...getDefaultVideoSettings(),
      title: video?.title || video?.name || '',
      duration: video?.durationSeconds || 0,
      endTime: video?.durationSeconds || 0,
    });
    toast.info('Settings reset to defaults');
  };

  // Drag to resize timeline
  const handleTimelineResizeStart = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = timelineHeightRef.current;
    const onMove = (mv) => {
      const delta = startY - mv.clientY;
      const newH = Math.max(120, Math.min(480, startH + delta));
      timelineHeightRef.current = newH;
      setTimelineHeight(newH);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Close editor — auto-save before closing
  const handleClose = async () => {
    if (hasChanges) await handleSave(true);
    onClose?.();
  };

  // Get module label
  const getModuleLabel = () => {
    switch (moduleType) {
      case 'youtube': return 'YouTube Editor';
      case 'reels': return 'Reels Editor';
      case 'talking': return 'Talking Video Editor';
      default: return 'Video Editor';
    }
  };

  // Live version of the selected text track
  const activeTextTrack = selectedTextTrack
    ? textTracks.find(t => t.id === selectedTextTrack.id) ?? selectedTextTrack
    : null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'hsl(var(--background))' }}>
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b" 
        style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface))' }}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <ChevronLeft size={18} />
          </Button>
          {editingTitle ? (
            <input
              autoFocus
              value={activeTextTrack ? (activeTextTrack.text || '') : (settings.title || '')}
              onChange={e => {
                if (activeTextTrack) {
                  setTextTracks(prev => prev.map(t => t.id === activeTextTrack.id ? { ...t, text: e.target.value } : t));
                } else {
                  handleSettingsChange({ ...settings, title: e.target.value });
                }
              }}
              onFocus={e => e.target.select()}
              onBlur={() => setEditingTitle(false)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingTitle(false); }}
              className="text-sm font-semibold bg-transparent border-b outline-none px-1"
              style={{ color: 'hsl(var(--foreground))', borderColor: 'hsl(var(--primary))', minWidth: '120px', maxWidth: '240px' }}
            />
          ) : (
            <button onClick={() => setEditingTitle(true)} className="text-left group">
              <h1 className="text-sm font-semibold group-hover:opacity-70 transition-opacity" style={{ color: 'hsl(var(--foreground))' }}>
                {activeTextTrack ? (activeTextTrack.text || 'Texto vacío') : (settings.title || 'Sin título')}
              </h1>
              <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                {activeTextTrack ? 'Texto seleccionado — click para editar' : `${getModuleLabel()} — click para editar`}
              </p>
            </button>
          )}
          {saving ? (
            <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>Guardando...</span>
          ) : hasChanges ? (
            <span className="text-xs px-2 py-0.5 rounded"
              style={{ background: 'hsl(var(--warning) / 0.2)', color: 'hsl(var(--warning))' }}>
              Sin guardar
            </span>
          ) : lastSaved ? (
            <span className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>Guardado</span>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {/* Reels: Add Clips button */}
          {moduleType === 'reels' && (
            <Button variant="outline" size="sm"
              data-testid="add-clips-btn"
              onClick={() => { setClipPickerOpen(true); setReelVideo(null); }}
              className="gap-1.5">
              <Plus size={14} /> Añadir Clips
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleReset}
            disabled={saving || exporting}
          >
            <RotateCcw size={16} className="mr-1" />
            Reset
          </Button>
          <Button 
            variant={timelineVisible ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setTimelineVisible(!timelineVisible)}
            title="Toggle Timeline"
          >
            {timelineVisible ? <PanelBottomClose size={16} className="mr-1" /> : <PanelBottomOpen size={16} className="mr-1" />}
            Timeline
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPreviewMode(!previewMode)}
          >
            <Eye size={16} className="mr-1" />
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <Loader2 size={16} className="mr-1 animate-spin" />
            ) : (
              <Save size={16} className="mr-1" />
            )}
            Guardar
          </Button>
          <Button 
            size="sm"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? (
              <Loader2 size={16} className="mr-1 animate-spin" />
            ) : (
              <Download size={16} className="mr-1" />
            )}
            Export
          </Button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">

        {/* ── Reel Generation Loading Overlay ── */}
        {reelGenerating && (
          <div className="absolute inset-0 z-[100] flex flex-col items-center justify-center"
            style={{ background: 'hsl(var(--background) / 0.97)' }}>
            <div className="text-7xl font-black mb-4 tabular-nums"
              style={{ color: 'hsl(var(--primary))', fontFamily: 'monospace', textShadow: '0 0 30px hsl(var(--primary) / 0.4)' }}>
              {Math.round(reelProgress)}%
            </div>
            <div className="w-64 h-1.5 rounded-full overflow-hidden mb-4" style={{ background: 'hsl(var(--border))' }}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${reelProgress}%`, background: 'linear-gradient(90deg, hsl(var(--primary)), #7c5cfc)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>{reelProgressMsg}</p>
            <p className="text-xs mt-2" style={{ color: 'hsl(var(--foreground-muted))' }}>No cierres la app</p>
          </div>
        )}

        {/* ── Clip Picker Panel (reels, no video yet) ── */}
        {clipPickerOpen && moduleType === 'reels' && !effectiveVideo && !reelGenerating && (
          <div className="flex-1 flex overflow-hidden">
            {/* Left: placeholder preview */}
            <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8"
              style={{ background: 'hsl(var(--background))' }}>
              <div className="w-32 h-56 rounded-2xl flex items-center justify-center"
                style={{ background: 'hsl(var(--surface))', border: '2px dashed hsl(var(--border))' }}>
                <Film size={36} style={{ color: 'hsl(var(--foreground-subtle))' }} />
              </div>
              <p className="text-sm text-center max-w-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>
                Selecciona clips de tu biblioteca y genera el reel automáticamente
              </p>
              {selectedClipIds.length > 0 && (
                <Button data-testid="generate-reel-from-picker-btn" onClick={handleGenerateReel}
                  className="gap-2 font-bold"
                  style={{ background: 'hsl(var(--primary))', color: '#000', boxShadow: '0 0 20px hsl(var(--primary)/0.35)' }}>
                  <Zap size={16} fill="currentColor" />
                  Generar Reel ({selectedClipIds.length} clips)
                </Button>
              )}
            </div>

            {/* Right: clip picker */}
            <div className="w-80 border-l flex flex-col" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface))' }}>
              <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'hsl(var(--border))' }}>
                <h3 className="text-sm font-bold" style={{ color: 'hsl(var(--foreground))' }}>
                  Biblioteca de clips
                </h3>
                {selectedClipIds.length > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                    style={{ background: 'hsl(var(--primary)/0.15)', color: 'hsl(var(--primary))' }}>
                    {selectedClipIds.length} sel.
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {libraryClips.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
                    <Film size={28} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                    <p className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>
                      No hay clips. Sube videos desde el Drive o el módulo de YouTube.
                    </p>
                    <Button size="sm" variant="outline" onClick={loadLibraryClips}>
                      <RefreshCw size={12} className="mr-1" /> Recargar
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {libraryClips.map((clip) => {
                      const isSelected = selectedClipIds.includes(clip.id);
                      const score = clipScores[clip.id]?.score;
                      const scoreColor = score >= 90 ? '#00d4aa' : score >= 75 ? '#7c5cfc' : '#f59e0b';
                      const selIdx = selectedClipIds.indexOf(clip.id);
                      return (
                        <div key={clip.id} data-testid={`picker-clip-${clip.id}`}
                          onClick={() => setSelectedClipIds(prev => prev.includes(clip.id) ? prev.filter(x => x !== clip.id) : [...prev, clip.id])}
                          className="relative cursor-pointer rounded-lg overflow-hidden"
                          style={{
                            aspectRatio: '9/16',
                            border: isSelected ? '2px solid hsl(var(--primary))' : '2px solid transparent',
                            background: 'hsl(var(--surface-elevated))',
                            boxShadow: isSelected ? '0 0 10px hsl(var(--primary)/0.3)' : 'none',
                          }}>
                          {clip.thumbnail_url
                            ? <img src={clip.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center"><Film size={14} style={{ color: 'hsl(var(--foreground-subtle))' }} /></div>
                          }
                          {/* Score */}
                          {score && (
                            <div className="absolute top-1 left-1 px-1 py-0 rounded text-xs font-bold"
                              style={{ background: 'rgba(0,0,0,0.75)', color: scoreColor, fontSize: 9 }}>
                              <Zap size={7} style={{ display: 'inline', marginRight: 1 }} />{score}
                            </div>
                          )}
                          {/* Selection number */}
                          {isSelected && (
                            <div className="absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center"
                              style={{ background: 'hsl(var(--primary))', color: '#000', fontSize: 9, fontWeight: 'bold' }}>
                              {selIdx + 1}
                            </div>
                          )}
                          {/* Duration */}
                          <div className="absolute bottom-0 left-0 right-0 text-center py-0.5"
                            style={{ background: 'rgba(0,0,0,0.7)', fontSize: 8, color: '#fff' }}>
                            {Math.round(clip.duration_seconds || 0)}s
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Footer */}
              {selectedClipIds.length > 0 && (
                <div className="p-3 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
                  <Button data-testid="generate-reel-btn" onClick={handleGenerateReel} className="w-full gap-2 font-bold"
                    style={{ background: 'hsl(var(--primary))', color: '#000' }}>
                    <Zap size={14} fill="currentColor" /> Generar Reel
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Normal Editor (when video exists) ── */}
        {(!clipPickerOpen || effectiveVideo) && !reelGenerating && (
          <>
        {/* Preview + Sidebar */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Preview Panel */}
          <div className="flex-1 flex items-center justify-center p-6 overflow-auto"
            style={{ background: 'hsl(var(--background))' }}>
            <VideoPreviewPanel
              videoUrl={effectiveVideo?.url}
              settings={settings}
              currentTime={currentTime}
              onTimeUpdate={setCurrentTime}
              textTracks={textTracks}
              onTextTracksChange={setTextTracks}
              playToggleRef={previewPlayRef}
              onPlayStateChange={setIsPlaying}
              clips={clips}
              musicClipInfo={musicClipState}
              className="w-full max-w-2xl"
            />
          </div>

          {/* Sidebar */}
          {!previewMode && (
            <VideoEditorSidebar
              settings={settings}
              onSettingsChange={handleSettingsChange}
              moduleType={moduleType}
              trialActive={trialActive}
              selectedTextTrack={activeTextTrack}
              onTextTracksChange={setTextTracks}
              onAutoSubtitles={handleAutoSubtitles}
              activeBrandId={activeBrand?.id}
              onAddMusicToTimeline={handleAddMusicToTimeline}
              className="w-80 border-l flex-shrink-0"
              style={{ borderColor: 'hsl(var(--border))' }}
            />
          )}
        </div>

        {/* Timeline Panel */}
        {timelineVisible && (
          <>
            {/* Drag handle */}
            <div
              className="h-1.5 flex-shrink-0 cursor-row-resize flex items-center justify-center group"
              style={{ background: 'hsl(var(--border))', borderTop: '1px solid hsl(var(--border))' }}
              onMouseDown={handleTimelineResizeStart}
              title="Arrastra para redimensionar el timeline"
            >
              <div className="w-10 h-0.5 rounded-full transition-colors group-hover:bg-primary"
                style={{ background: 'hsl(var(--foreground-subtle))' }} />
            </div>
            <TimelinePanel
              clips={clips}
              onClipsChange={setClips}
              brollClips={brollClips}
              onBrollChange={setBrollClips}
              textTracks={textTracks}
              onTextTracksChange={setTextTracks}
              duration={settings.duration || 60}
              currentTime={currentTime}
              onTimeChange={setCurrentTime}
              onAutoSubtitles={handleAutoSubtitles}
              onTextSelectionChange={setSelectedTextTrack}
              thumbnailBaseUrl={effectiveVideo?.id ? `${process.env.REACT_APP_BACKEND_URL}/api/youtube/clip-thumbnail/${effectiveVideo.id}?auth=${getAuthToken()}&time_seconds=` : null}
              thumbnailStartTime={effectiveVideo?.originalStartTime || 0}
              videoId={effectiveVideo?.id || null}
              videoOriginalStartTime={effectiveVideo?.originalStartTime || 0}
              isPlaying={isPlaying}
              onPlayToggle={handlePlayToggle}
              musicTrack={timelineMusicTrack}
              onMusicClipChange={setMusicClipState}
              style={{ height: `${timelineHeight}px` }}
              className="flex-shrink-0"
            />
          </>
        )}
          </>
        )}
      </div>

      {/* Export warning for trial users */}
      {trialActive && (
        <div className="px-4 py-2 flex items-center justify-center gap-2"
          style={{ background: 'hsl(var(--warning) / 0.1)', borderTop: '1px solid hsl(var(--border))' }}>
          <AlertCircle size={14} style={{ color: 'hsl(var(--warning))' }} />
          <span className="text-xs" style={{ color: 'hsl(var(--warning))' }}>
            Trial mode: Exports include watermark. Upgrade for watermark-free HD exports.
          </span>
        </div>
      )}

      {/* Auto Subtitles Dialog */}
      {subsOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={() => { if (!subsGenerating) setSubsOpen(false); }}>
          <div className="w-full max-w-sm rounded-xl p-6 flex flex-col gap-4"
            style={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
            onClick={e => e.stopPropagation()}>
            {subsGenerating ? (
              <>
                <h3 className="text-base font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Generando subtítulos...</h3>
                <div className="flex items-center gap-3">
                  <Loader2 size={20} className="animate-spin" style={{ color: 'hsl(var(--primary))' }} />
                  <span className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>Analizando audio con IA</span>
                </div>
              </>
            ) : subsConfirm ? (
              <>
                <h3 className="text-base font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Ya tienes subtítulos</h3>
                <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>
                  Hay {textTracks.length} subtítulos existentes. ¿Qué deseas hacer?
                </p>
                <div className="flex flex-col gap-2">
                  <Button size="sm" onClick={() => doGenerate(false)} className="w-full">Mantener y añadir nuevos</Button>
                  <Button size="sm" variant="destructive" onClick={() => doGenerate(true)} className="w-full">Reemplazar todos</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setSubsOpen(false); setSubsConfirm(false); }} className="w-full">Cancelar</Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-base font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Subtítulos automáticos</h3>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: 'hsl(var(--foreground-muted))' }}>Idioma</label>
                  <select value={subsLang} onChange={e => setSubsLang(e.target.value)}
                    className="w-full h-9 px-3 rounded-md text-sm border"
                    style={{ background: 'hsl(var(--surface))', color: 'hsl(var(--foreground))', borderColor: 'hsl(var(--border))' }}>
                    {CAPTION_LANGUAGES.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1" onClick={() => { if (textTracks.length > 0) { setSubsConfirm(true); } else { doGenerate(false); } }}>
                    Generar subtítulos
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSubsOpen(false)}>Cancelar</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoEditorPage;
